import {
  BarSize,
  FtIn,
  createLine,
  type BarClass,
  type FunctionCode,
  type Line,
  type MaterialType,
  type Segment,
} from '@otto/rebar-engine';

export function cloneSegs(segments: Segment[]): Segment[] {
  return segments.map((s) => ({ ...s, lines: s.lines.map((l) => ({ ...l })) }));
}

export interface TreeRow {
  seg: Segment;
  depth: number;
}

export function buildTree(segments: Segment[]): TreeRow[] {
  const childrenOf = new Map<string, Segment[]>();
  for (const s of segments) {
    if (s.parentSegmentId) {
      const list = childrenOf.get(s.parentSegmentId) ?? [];
      list.push(s);
      childrenOf.set(s.parentSegmentId, list);
    }
  }
  const result: TreeRow[] = [];
  function visit(seg: Segment, depth: number) {
    result.push({ seg, depth });
    const children = (childrenOf.get(seg.segmentId) ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);
    for (const c of children) visit(c, depth + 1);
  }
  const roots = segments
    .filter((s) => !s.parentSegmentId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  for (const r of roots) visit(r, 0);
  return result;
}

export function getSiblings(segments: Segment[], seg: Segment): Segment[] {
  // Engine types parentSegmentId as `string | null`. Normalize both sides
  // with `?? null` so a caller that ever drifts to `undefined` for roots
  // still groups correctly with engine-emitted `null`.
  const target = seg.parentSegmentId ?? null;
  return segments
    .filter((s) => (s.parentSegmentId ?? null) === target)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export type FlatRow =
  | { kind: 'segment'; id: string; segmentId: string; depth: number }
  | { kind: 'line'; id: string; segmentId: string; lineNumber: number; depth: number };

export const segmentRowId = (segmentId: string) => `seg:${segmentId}`;
export const lineRowId = (segmentId: string, lineNumber: number) =>
  `line:${segmentId}:${lineNumber}`;

export function flattenRows(segments: Segment[], expanded: Set<string>): FlatRow[] {
  const tree = buildTree(segments);
  const rows: FlatRow[] = [];
  for (const { seg, depth } of tree) {
    rows.push({ kind: 'segment', id: segmentRowId(seg.segmentId), segmentId: seg.segmentId, depth });
    if (expanded.has(seg.segmentId)) {
      for (const line of seg.lines) {
        rows.push({
          kind: 'line',
          id: lineRowId(seg.segmentId, line.lineNumber),
          segmentId: seg.segmentId,
          lineNumber: line.lineNumber,
          depth: depth + 1,
        });
      }
    }
  }
  return rows;
}

export function parseFtIn(s: string): FtIn | null {
  if (!s.trim()) return null;
  try {
    return FtIn.parse(s.trim());
  } catch {
    return null;
  }
}

export function parseBarSize(s: string): BarSize | null {
  if (!s.trim()) return null;
  try {
    return BarSize.parse(s.trim());
  } catch {
    return null;
  }
}

export function computeLinealFeet(line: Line): number | null {
  const dims = [line.L1, line.L2, line.L3, line.L4, line.L5].filter(
    (d): d is FtIn => d !== null,
  );
  if (dims.length === 0) return null;
  const unitFt = dims.reduce((acc, d) => acc + d.toDecimalFeet(), 0);
  return unitFt * line.quantity * line.multiplier;
}

export function newSegmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `seg_${Math.random().toString(36).slice(2, 10)}`;
}

// --- Bulk paste from tab-delimited text ---
// Column order: Line# | Material | Function | Size | Class | Qty | Mult | L1 | L2 | L3 | L4 | L5 | Lap | Notes
// All columns except Line# are optional; missing values use safe defaults.
const PASTE_HEADER_TOKENS = new Set(['line', 'line#', 'line #', '#', 'no', 'no.']);
const MATERIALS: ReadonlySet<MaterialType> = new Set(['TR', 'GI', 'MT', 'TP', 'TI']);
const FUNCTIONS: ReadonlySet<FunctionCode> = new Set([
  'BF',
  'TC',
  'TA',
  'TB',
  'TD',
  'SPX',
  'RA',
  'Sheets',
]);
const BAR_CLASSES: ReadonlySet<BarClass> = new Set(['S', 'L', 'H']);

function parseQty(s: string | undefined, fallback: number): number {
  if (!s) return fallback;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : fallback;
}

export function parseLinesFromText(
  text: string,
  segmentId: string,
  existingLineNumbers: Set<number>,
): { lines: Line[]; errors: string[] } {
  const errors: string[] = [];
  const lines: Line[] = [];
  const rawRows = text.split(/\r?\n/).filter((r) => r.trim().length > 0);
  if (rawRows.length === 0) return { lines, errors };

  let startIdx = 0;
  const firstCell = (rawRows[0].split('\t')[0] ?? '').trim().toLowerCase();
  if (PASTE_HEADER_TOKENS.has(firstCell)) startIdx = 1;

  const used = new Set(existingLineNumbers);
  let nextAuto = 1;
  while (used.has(nextAuto)) nextAuto++;

  for (let i = startIdx; i < rawRows.length; i++) {
    const cols = rawRows[i].split('\t').map((c) => c.trim());
    const [
      rawLineNum,
      rawMat,
      rawFn,
      rawSize,
      rawClass,
      rawQty,
      rawMult,
      rawL1,
      rawL2,
      rawL3,
      rawL4,
      rawL5,
      rawLap,
      rawNotes,
    ] = cols;

    let lineNumber: number;
    if (rawLineNum && /^\d+$/.test(rawLineNum) && !used.has(parseInt(rawLineNum, 10))) {
      lineNumber = parseInt(rawLineNum, 10);
    } else {
      while (used.has(nextAuto)) nextAuto++;
      lineNumber = nextAuto;
    }
    used.add(lineNumber);

    const sizeStr = (rawSize || '').replace(/^#/, '');
    const tryParseFtIn = (s?: string) => {
      if (!s) return null;
      try {
        return FtIn.parse(s);
      } catch {
        throw new Error(`bad dimension "${s}"`);
      }
    };

    try {
      const matRaw = (rawMat || 'TR').toUpperCase();
      if (!MATERIALS.has(matRaw as MaterialType)) {
        throw new Error(`unknown material "${rawMat}"`);
      }
      const fnRaw = rawFn || 'BF';
      if (!FUNCTIONS.has(fnRaw as FunctionCode)) {
        throw new Error(`unknown function "${rawFn}"`);
      }
      const classRaw = (rawClass || 'S').toUpperCase();
      if (!BAR_CLASSES.has(classRaw as BarClass)) {
        throw new Error(`unknown class "${rawClass}"`);
      }
      const line = createLine({
        lineNumber,
        segmentId,
        material: matRaw as MaterialType,
        function: fnRaw as FunctionCode,
        size: sizeStr ? BarSize.parse(sizeStr) : null,
        class: classRaw as BarClass,
        quantity: parseQty(rawQty, 1),
        multiplier: parseQty(rawMult, 1),
        L1: tryParseFtIn(rawL1),
        L2: tryParseFtIn(rawL2),
        L3: tryParseFtIn(rawL3),
        L4: tryParseFtIn(rawL4),
        L5: tryParseFtIn(rawL5),
        lap: rawLap || null,
        notes: rawNotes || '',
      });
      lines.push(line);
    } catch (e) {
      errors.push(`Row ${i + 1}: ${(e as Error).message}`);
    }
  }

  return { lines, errors };
}

// --- Line form state ---

export interface LineFormState {
  lineNumber: string;
  material: MaterialType;
  function: FunctionCode;
  size: string;
  class: BarClass;
  quantity: string;
  multiplier: string;
  placing: string;
  grade: string;
  span: string;
  spacing: string;
  L1: string;
  L2: string;
  L3: string;
  L4: string;
  L5: string;
  lap: string;
  stockLength: string;
  notes: string;
}

export function emptyLineForm(nextLineNumber: number): LineFormState {
  return {
    lineNumber: String(nextLineNumber),
    material: 'TR',
    function: 'BF',
    size: '',
    class: 'S',
    quantity: '1',
    multiplier: '1',
    placing: '',
    grade: '',
    span: '',
    spacing: '',
    L1: '',
    L2: '',
    L3: '',
    L4: '',
    L5: '',
    lap: '',
    stockLength: '',
    notes: '',
  };
}

export function lineToForm(line: Line): LineFormState {
  return {
    lineNumber: String(line.lineNumber),
    material: line.material,
    function: line.function,
    size: line.size ? `#${line.size.imperial}` : '',
    class: line.class ?? 'S',
    quantity: String(line.quantity),
    multiplier: String(line.multiplier),
    placing: line.placing,
    grade: line.grade !== null ? String(line.grade) : '',
    span: line.span ? line.span.toString() : '',
    spacing: line.spacing ? line.spacing.toString() : '',
    L1: line.L1?.toString() ?? '',
    L2: line.L2?.toString() ?? '',
    L3: line.L3?.toString() ?? '',
    L4: line.L4?.toString() ?? '',
    L5: line.L5?.toString() ?? '',
    lap: line.lap ?? '',
    stockLength: line.stockLength ? line.stockLength.toString() : '',
    notes: line.notes,
  };
}

export function formToLine(form: LineFormState, segmentId: string): Line | null {
  const lineNumber = parseInt(form.lineNumber, 10);
  if (isNaN(lineNumber)) return null;
  const grade = form.grade.trim() ? parseInt(form.grade.trim(), 10) : null;
  return createLine({
    lineNumber,
    segmentId,
    function: form.function,
    material: form.material,
    size: parseBarSize(form.size),
    class: form.class,
    quantity: parseQty(form.quantity, 0),
    multiplier: parseQty(form.multiplier, 1),
    placing: form.placing,
    grade: grade !== null && !isNaN(grade) ? grade : null,
    span: parseFtIn(form.span),
    spacing: parseFtIn(form.spacing),
    L1: parseFtIn(form.L1),
    L2: parseFtIn(form.L2),
    L3: parseFtIn(form.L3),
    L4: parseFtIn(form.L4),
    L5: parseFtIn(form.L5),
    lap: form.lap.trim() || null,
    stockLength: parseFtIn(form.stockLength),
    notes: form.notes,
  });
}
